import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '../services/api';
import { 
  ClockIcon,
  UserIcon,
  FunnelIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

const AdminActivity = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filters, setFilters] = useState({
    user_id: '',
    action_type: '',
    date_from: '',
    date_to: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState('activity_logs');
  const [deleteDays, setDeleteDays] = useState(30);

  const handleActivityClick = (log) => {
    // 활동 타입에 따라 적절한 페이지로 이동
    if (log.project_id) {
      // 프로젝트 관련 활동
      if (log.action_type === 'message_send') {
        navigate(`/projects/${log.project_id}?tab=chat`);
      } else {
        navigate(`/projects/${log.project_id}`);
      }
    } else if (log.target_type === 'project' && log.target_id) {
      // target_id로 프로젝트 참조
      navigate(`/projects/${log.target_id}`);
    } else if (log.target_type === 'notice' && log.target_id) {
      // 공지사항 관련
      navigate(`/admin/notices`);
    } else if (log.target_type === 'help' && log.target_id) {
      // 도움말 관련
      navigate(`/admin/help`);
    }
  };

  const { data: activityData, isLoading, refetch } = useQuery(
    ['activity-logs', page, pageSize, filters],
    () => api.get('/analytics/activity-logs', {
      params: {
        page,
        limit: pageSize,
        ...filters
      }
    }).then(res => res.data),
    {
      keepPreviousData: true
    }
  );

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleDeleteOldData = async () => {
    try {
      console.log('Deleting activity data:', { days: deleteDays, type: deleteType });
      const response = await api.delete('/analytics/cleanup', {
        data: {
          days: deleteDays,
          type: deleteType
        }
      });
      console.log('Delete response:', response.data);
      
      toast.success(`${deleteDays}일 이전 ${deleteType === 'activity_logs' ? '활동 로그' : '페이지 접속 기록'}이 삭제되었습니다.`);
      setShowDeleteModal(false);
      refetch();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(`데이터 삭제에 실패했습니다: ${error.response?.data?.error || error.message}`);
    }
  };

  const getActionTypeLabel = (type) => {
    const labels = {
      login: '로그인',
      logout: '로그아웃',
      project_create: '프로젝트 생성',
      project_update: '프로젝트 수정',
      project_delete: '프로젝트 삭제',
      message_send: '메시지 전송',
      file_upload: '파일 업로드',
      notice_create: '공지사항 생성',
      notice_update: '공지사항 수정',
      notice_delete: '공지사항 삭제',
      help_create: '도움말 생성',
      help_update: '도움말 수정',
      help_delete: '도움말 삭제'
    };
    return labels[type] || type;
  };

  const getUserTypeLabel = (type) => {
    const labels = {
      admin: '관리자',
      contractor: '소장',
      customer: '고객',
      vip_customer: 'VIP 고객'
    };
    return labels[type] || type;
  };

  const getUserTypeBadgeColor = (type) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      contractor: 'bg-blue-100 text-blue-800',
      customer: 'bg-green-100 text-green-800',
      vip_customer: 'bg-yellow-100 text-yellow-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">활동 기록</h2>
          <p className="text-sm text-gray-600 mt-1">시스템 전체 활동 로그를 확인합니다</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">표시 개수:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value={25}>25개</option>
              <option value={50}>50개</option>
              <option value={100}>100개</option>
              <option value={200}>200개</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters ? 'bg-blue-100 text-blue-700' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FunnelIcon className="h-5 w-5" />
              필터
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <TrashIcon className="h-5 w-5" />
              데이터 정리
            </button>
          </div>
        </div>
      </div>

      {/* 필터 섹션 */}
      {showFilters && (
        <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                활동 유형
              </label>
              <select
                value={filters.action_type}
                onChange={(e) => handleFilterChange('action_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">전체</option>
                <option value="login">로그인</option>
                <option value="logout">로그아웃</option>
                <option value="project_create">프로젝트 생성</option>
                <option value="project_update">프로젝트 수정</option>
                <option value="project_delete">프로젝트 삭제</option>
                <option value="message_send">메시지 전송</option>
                <option value="file_upload">파일 업로드</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                시작일
              </label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                종료일
              </label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                사용자 검색
              </label>
              <input
                type="text"
                value={filters.user_id}
                onChange={(e) => handleFilterChange('user_id', e.target.value)}
                placeholder="ID, 로그인ID, 이메일 검색"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* 활동 로그 - 모바일 카드 레이아웃 */}
      <div className="block lg:hidden space-y-4">
        {isLoading ? (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : activityData?.logs?.length > 0 ? (
          activityData.logs.map((log) => (
            <div 
              key={log.id} 
              className="bg-white rounded-lg shadow overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => handleActivityClick(log)}
            >
              <div className="p-4">
                {/* 헤더 */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getUserTypeBadgeColor(log.user_type)}`}>
                      {getUserTypeLabel(log.user_type)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {format(new Date(log.created_at), 'MM/dd HH:mm:ss', { locale: ko })}
                    </span>
                  </div>
                </div>

                {/* 사용자 정보 */}
                <div className="mb-3">
                  <div className="text-sm font-semibold text-gray-900">
                    {log.user_name || 'Unknown'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {(() => {
                      if (log.login_id) return log.login_id;
                      if (log.email) {
                        if (typeof log.email === 'string') return log.email;
                        if (typeof log.email === 'object' && log.email.email) return log.email.email;
                      }
                      return `ID: ${log.user_id}`;
                    })()}
                  </div>
                  {log.company_name && (
                    <div className="text-xs text-gray-500 mt-1">{log.company_name}</div>
                  )}
                </div>

                {/* 활동 정보 */}
                <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-700 mb-1">활동</div>
                  <div className="text-sm text-gray-900">
                    {getActionTypeLabel(log.action_type)}
                  </div>
                </div>

                {/* 상세 정보 */}
                <div className="mb-3">
                  <div className="text-xs text-gray-500 mb-1">상세</div>
                  <div className="text-sm text-gray-700 break-words">
                    {(() => {
                      const details = log.details || log.old_value || log.new_value;
                      if (!details) return '-';
                      if (typeof details === 'string') return details;
                      if (typeof details === 'object') {
                        try {
                          return JSON.stringify(details);
                        } catch (e) {
                          return '-';
                        }
                      }
                      return String(details);
                    })()}
                  </div>
                </div>

                {/* IP 주소 */}
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-500">IP:</span>
                    <span className="text-gray-700">{log.ip_address}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
            활동 로그가 없습니다.
          </div>
        )}
      </div>

      {/* 활동 로그 - PC 테이블 레이아웃 */}
      <div className="hidden lg:block bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      시간
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      사용자
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      활동
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      상세
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      IP 주소
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activityData?.logs?.map((log) => (
                    <tr 
                      key={log.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleActivityClick(log)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {format(new Date(log.created_at), 'MM/dd HH:mm:ss', { locale: ko })}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {log.user_name || 'Unknown'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {(() => {
                                if (log.login_id) return log.login_id;
                                if (log.email) {
                                  if (typeof log.email === 'string') return log.email;
                                  if (typeof log.email === 'object' && log.email.email) return log.email.email;
                                }
                                return `ID: ${log.user_id}`;
                              })()}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getUserTypeBadgeColor(log.user_type)}`}>
                                {getUserTypeLabel(log.user_type)}
                              </span>
                              {log.company_name && (
                                <span className="text-xs text-gray-500">{log.company_name}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">
                          {getActionTypeLabel(log.action_type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        <div className="max-w-xs truncate" title={(() => {
                          const details = log.details || log.old_value || log.new_value;
                          if (!details) return '-';
                          if (typeof details === 'string') return details;
                          if (typeof details === 'object') {
                            try {
                              return JSON.stringify(details);
                            } catch (e) {
                              return '-';
                            }
                          }
                          return String(details);
                        })()}>
                          {(() => {
                            const details = log.details || log.old_value || log.new_value;
                            if (!details) return '-';
                            if (typeof details === 'string') return details;
                            if (typeof details === 'object') {
                              try {
                                return JSON.stringify(details);
                              } catch (e) {
                                return '-';
                              }
                            }
                            return String(details);
                          })()}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {log.ip_address}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </>
        )}
      </div>

      {/* 페이지네이션 */}
      {activityData?.pages > 1 && (
        <div className="bg-white rounded-lg shadow mt-4 px-4 py-3 flex items-center justify-between sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
              disabled={page === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              이전
            </button>
            <button
              onClick={() => setPage(prev => Math.min(activityData.pages, prev + 1))}
              disabled={page === activityData.pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              다음
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                총 <span className="font-medium">{activityData.total}</span>개 중{' '}
                <span className="font-medium">{(page - 1) * pageSize + 1}</span> -{' '}
                <span className="font-medium">{Math.min(page * pageSize, activityData.total)}</span> 표시
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">이전</span>
                  ◀
                </button>
                
                {(() => {
                  const totalPages = activityData.pages;
                  const maxButtons = 7;
                  let startPage = Math.max(1, page - Math.floor(maxButtons / 2));
                  let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                  
                  if (endPage - startPage < maxButtons - 1) {
                    startPage = Math.max(1, endPage - maxButtons + 1);
                  }
                  
                  const pageButtons = [];
                  
                  if (startPage > 1) {
                    pageButtons.push(
                      <button
                        key={1}
                        onClick={() => setPage(1)}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pageButtons.push(
                        <span key="ellipsis1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      );
                    }
                  }
                  
                  for (let i = startPage; i <= endPage; i++) {
                    pageButtons.push(
                      <button
                        key={i}
                        onClick={() => setPage(i)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          i === page
                            ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pageButtons.push(
                        <span key="ellipsis2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>
                      );
                    }
                    pageButtons.push(
                      <button
                        key={totalPages}
                        onClick={() => setPage(totalPages)}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        {totalPages}
                      </button>
                    );
                  }
                  
                  return pageButtons;
                })()}
                
                <button
                  onClick={() => setPage(Math.min(activityData.pages, page + 1))}
                  disabled={page === activityData.pages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <span className="sr-only">다음</span>
                  ▶
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* 데이터 삭제 모달 */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              <h3 className="text-lg font-bold">오래된 데이터 삭제</h3>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  삭제할 데이터 유형
                </label>
                <select
                  value={deleteType}
                  onChange={(e) => setDeleteType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="activity_logs">활동 로그</option>
                  <option value="page_views">페이지 접속 기록</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  보관 기간
                </label>
                <select
                  value={deleteDays}
                  onChange={(e) => setDeleteDays(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value={1}>1일 이전 데이터 삭제</option>
                  <option value={3}>3일 이전 데이터 삭제</option>
                  <option value={7}>7일 이전 데이터 삭제</option>
                  <option value={15}>15일 이전 데이터 삭제</option>
                  <option value={30}>30일 이전 데이터 삭제</option>
                  <option value={60}>60일 이전 데이터 삭제</option>
                  <option value={90}>90일 이전 데이터 삭제</option>
                  <option value={180}>180일 이전 데이터 삭제</option>
                  <option value={365}>365일 이전 데이터 삭제</option>
                </select>
              </div>
              <p className="text-sm text-gray-600">
                선택한 기간보다 오래된 {deleteType === 'activity_logs' ? '활동 로그' : '페이지 접속 기록'}가 
                영구적으로 삭제됩니다. 이 작업은 되돌릴 수 없습니다.
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                취소
              </button>
              <button
                onClick={handleDeleteOldData}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminActivity;